import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import StudioSelector from "./components/StudioSelector";
import CalendarView from "./components/CalendarView";
import BookingForm from "./components/BookingForm";
import ConfirmationModal from "./components/ConfirmationModal";
import { getStudios } from "./services/api";
import { tokenManager } from "./services/tokenManager";
import { Studio, TimeSlot, BookingData } from "./types";
import { environment } from "./config/environment";
import "./index.css";

function App(): JSX.Element {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [studios, setStudios] = useState<Studio[]>([]);
  const [selectedStudios, setSelectedStudios] = useState<string[]>([]);
  const [selectedSlots, setSelectedSlots] = useState<TimeSlot[]>([]);
  const [bookingData, setBookingData] = useState<BookingData | null>(null);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState<boolean>(true);

  const initializeApp = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setTokenLoading(true);

      // Initialize token manager (this will fetch initial token)
      await tokenManager.getToken();
      setTokenLoading(false);

      // Load studios
      await loadStudios();
    } catch (err) {
      console.error("Error initializing app:", err);
      setError("Failed to initialize application");
      setTokenLoading(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    initializeApp();
  }, [initializeApp]);

  const loadStudios = async (): Promise<void> => {
    try {
      const data = await getStudios();
      setStudios(data.studios);
    } catch (err) {
      setError("Failed to load studios");
      console.error("Error loading studios:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStudioSelection = (studioIds: string[]): void => {
    setSelectedStudios(studioIds);
    setCurrentStep(2);
  };

  const handleSlotSelection = (slots: TimeSlot[]): void => {
    setSelectedSlots(slots);
    setCurrentStep(3);
  };

  const handleBookingData = (data: BookingData): void => {
    setBookingData(data);
    setShowConfirmation(true);
    // Scroll to top when opening confirmation modal
    scrollToTop();
  };

  const handleBookingComplete = (): void => {
    // Reset form and go back to step 1
    setCurrentStep(1);
    setSelectedStudios([]);
    setSelectedSlots([]);
    setBookingData(null);
    setShowConfirmation(false);
  };

  const handleEditBooking = (): void => {
    // Just close the modal without resetting the application state
    setShowConfirmation(false);
    // Scroll to top when editing booking
    scrollToTop();
  };

  const handleBack = (): void => {
    if (currentStep > 1) {
      // Only reset selected slots and studios when going back to studio selection from calendar
      if (currentStep === 2) {
        setSelectedSlots([]);
        setSelectedStudios([]);
      }
      setCurrentStep(currentStep - 1);
    }
  };

  // Helper function to scroll to top (handles both iframe and non-iframe scenarios)
  const scrollToTop = (): void => {
    // Scroll the iframe itself
    window.scrollTo({ top: 0, behavior: "smooth" });

    // If in an iframe, also notify parent window to scroll page to top (to show header)
    if (window.parent !== window) {
      window.parent.postMessage(
        {
          type: "iframe-scroll-to-top",
        },
        "*" // In production, replace '*' with the specific origin
      );
    }
  };

  // Scroll to top when navigating between steps
  useEffect(() => {
    scrollToTop();
  }, [currentStep]);

  // Communicate iframe height to parent window for dynamic resizing
  useEffect(() => {
    const sendHeightToParent = () => {
      const height = document.documentElement.scrollHeight;

      // Send height to parent window via postMessage
      if (window.parent !== window) {
        window.parent.postMessage(
          {
            type: "iframe-height",
            height: height,
          },
          "*" // In production, replace '*' with the specific origin
        );
      }
    };

    // Send initial height
    sendHeightToParent();

    // Use ResizeObserver to detect content changes
    const resizeObserver = new ResizeObserver(() => {
      sendHeightToParent();
    });

    resizeObserver.observe(document.body);

    // Send height on state changes with a slight delay for animations
    const timeoutId = setTimeout(() => {
      sendHeightToParent();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [currentStep, loading, error, tokenLoading, showConfirmation]);

  if (loading || tokenLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "hsla(var(--lightAccent-hsl), 1)" }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-primary-600">
            {tokenLoading ? "Initializing..." : "Loading studios..."}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "hsla(var(--lightAccent-hsl), 1)" }}
      >
        <div className="text-center">
          <div className="text-primary-700 text-xl mb-4">⚠️</div>
          <p className="text-primary-700 mb-4">{error}</p>
          <button
            onClick={initializeApp}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={environment.recaptchaSiteKey}
      scriptProps={{
        async: false,
        defer: false,
        appendTo: "head",
        nonce: undefined,
      }}
    >
      <Router>
        <div
          className="min-h-screen"
          style={{ backgroundColor: "hsla(var(--lightAccent-hsl), 1)" }}
        >
          <header
            style={{ backgroundColor: "hsla(var(--lightAccent-hsl), 1)" }}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center py-4 sm:py-6">
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="text-xs sm:text-sm text-primary-600">
                    Step {currentStep} of 3
                  </div>
                  <div className="w-24 sm:w-32 bg-primary-200 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(currentStep / 3) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <Routes>
              <Route
                path="/"
                element={
                  <div className="space-y-6 sm:space-y-8">
                    {currentStep === 1 && (
                      <StudioSelector
                        studios={studios}
                        selectedStudios={selectedStudios}
                        onSelection={handleStudioSelection}
                        onBack={handleBack}
                        currentStep={currentStep}
                      />
                    )}

                    {currentStep === 2 && (
                      <CalendarView
                        studios={studios}
                        selectedStudios={selectedStudios}
                        selectedSlots={selectedSlots}
                        onSlotSelection={handleSlotSelection}
                        onBack={handleBack}
                        initialSelectedDate={
                          selectedSlots.length > 0
                            ? new Date(selectedSlots[0].start)
                            : undefined
                        }
                        initialCurrentMonth={
                          selectedSlots.length > 0
                            ? new Date(selectedSlots[0].start)
                            : undefined
                        }
                      />
                    )}

                    {currentStep === 3 && (
                      <BookingForm
                        selectedStudios={studios.filter((studio) =>
                          selectedStudios.includes(studio.id)
                        )}
                        selectedSlots={selectedSlots}
                        onBookingData={handleBookingData}
                        onBack={handleBack}
                      />
                    )}
                  </div>
                }
              />
            </Routes>
          </main>

          {showConfirmation && bookingData && (
            <ConfirmationModal
              bookingData={bookingData}
              selectedStudios={studios.filter((studio) =>
                selectedStudios.includes(studio.id)
              )}
              selectedSlots={selectedSlots}
              onComplete={handleBookingComplete}
              onClose={handleBookingComplete}
              onEdit={handleEditBooking}
            />
          )}
        </div>
      </Router>
    </GoogleReCaptchaProvider>
  );
}

export default App;
